/**
 * career-runs — the KI-Läufe surface (route `/runs`, §8 / ADR-018 Part 1). An
 * owner-scoped, COLD-READ projection of the per-user `run.Captured` stream: every
 * AI call disclosed turn-by-turn. A summary ledger (Läufe / Aufrufe / Tokens /
 * EUR-Gesamt @ the server rate), then a two-level native `<details>` disclosure —
 * each Run (activity + when + rolled-up cost) → each RunStep (model / engine,
 * token metrics, the EUR cost-split, a params grid, and the prompt messages in
 * `<pre>` blocks with a copy affordance + a "gekürzt" note). Read-only; no store,
 * no push — a single `/api/runs` fetch (newest-first from the server).
 *
 * `costUsd` stays the source of truth (providers bill USD); EUR is an estimate shown
 * with the server's configured static rate. Subscription engines (claude / codex)
 * and deterministic steps cost €0 → "Kostenfrei · Abo €0".
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { html, nothing, repeat, type TemplateResult } from "@platform/web-kit/render"
import { I18nToken, apiGet } from "../services"
import type { CareerI18n, CareerKey } from "../../i18n"

// ─── Client view types (parsed from the untrusted wire record) ──────────────────

type RunEngine = "synthetic" | "noop" | "openrouter" | "codex" | "claude"

interface RunPromptMsg {
  role: string
  content: string
}
interface RunParams {
  temperature?: number
  topP?: number
  maxTokens?: number
  reasoningEffort?: string
  jsonSchemaName?: string
  jsonSchemaStrict?: boolean
}
interface RunStepView {
  model: string
  engine: RunEngine
  promptTokens?: number
  completionTokens?: number
  cachedTokens?: number
  costUsd: number
  latencyMs?: number
  cacheHit?: boolean
  label?: string
  purpose?: string
  prompt?: RunPromptMsg[]
  promptTruncated?: boolean
  params?: RunParams
  contextNote?: string
}
interface RunView {
  id: string
  activityType?: string
  steps: RunStepView[]
  createdAt: string
}

const num = (v: unknown): number | undefined => (typeof v === "number" ? v : undefined)
const str = (v: unknown): string | undefined => (typeof v === "string" ? v : undefined)

function parseParams(raw: unknown): RunParams | undefined {
  if (!raw || typeof raw !== "object") return undefined
  const p = raw as Record<string, unknown>
  const out: RunParams = {
    ...(num(p["temperature"]) !== undefined ? { temperature: num(p["temperature"]) } : {}),
    ...(num(p["topP"]) !== undefined ? { topP: num(p["topP"]) } : {}),
    ...(num(p["maxTokens"]) !== undefined ? { maxTokens: num(p["maxTokens"]) } : {}),
    ...(str(p["reasoningEffort"]) ? { reasoningEffort: str(p["reasoningEffort"]) } : {}),
    ...(str(p["jsonSchemaName"]) ? { jsonSchemaName: str(p["jsonSchemaName"]) } : {}),
    ...(typeof p["jsonSchemaStrict"] === "boolean" ? { jsonSchemaStrict: p["jsonSchemaStrict"] } : {}),
  }
  return Object.keys(out).length > 0 ? out : undefined
}

function parseStep(raw: unknown): RunStepView | null {
  if (!raw || typeof raw !== "object") return null
  const s = raw as Record<string, unknown>
  const model = str(s["model"])
  const engine = str(s["engine"]) as RunEngine | undefined
  if (!model || !engine) return null
  const prompt = Array.isArray(s["prompt"])
    ? (s["prompt"] as unknown[]).flatMap((m): RunPromptMsg[] => {
        const mm = (m ?? {}) as Record<string, unknown>
        const role = str(mm["role"])
        const content = str(mm["content"])
        return role !== undefined && content !== undefined ? [{ role, content }] : []
      })
    : undefined
  return {
    model,
    engine,
    ...(num(s["promptTokens"]) !== undefined ? { promptTokens: num(s["promptTokens"]) } : {}),
    ...(num(s["completionTokens"]) !== undefined ? { completionTokens: num(s["completionTokens"]) } : {}),
    ...(num(s["cachedTokens"]) !== undefined ? { cachedTokens: num(s["cachedTokens"]) } : {}),
    costUsd: num(s["costUsd"]) ?? 0,
    ...(num(s["latencyMs"]) !== undefined ? { latencyMs: num(s["latencyMs"]) } : {}),
    ...(typeof s["cacheHit"] === "boolean" ? { cacheHit: s["cacheHit"] } : {}),
    ...(str(s["label"]) ? { label: str(s["label"]) } : {}),
    ...(str(s["purpose"]) ? { purpose: str(s["purpose"]) } : {}),
    ...(prompt && prompt.length > 0 ? { prompt } : {}),
    ...(s["promptTruncated"] === true ? { promptTruncated: true } : {}),
    ...(parseParams(s["params"]) ? { params: parseParams(s["params"]) } : {}),
    ...(str(s["contextNote"]) ? { contextNote: str(s["contextNote"]) } : {}),
  }
}

function parseRun(raw: unknown): RunView | null {
  if (!raw || typeof raw !== "object") return null
  const r = raw as Record<string, unknown>
  const id = str(r["id"])
  const createdAt = str(r["createdAt"])
  if (!id || !createdAt) return null
  const steps = Array.isArray(r["steps"]) ? (r["steps"] as unknown[]).map(parseStep).filter((s): s is RunStepView => s !== null) : []
  return { id, ...(str(r["activityType"]) ? { activityType: str(r["activityType"]) } : {}), steps, createdAt }
}

// ─── Vocabulary (engine + activity + turn labels; raw code token as fallback) ───

const ENGINE_KEY: Record<RunEngine, CareerKey> = {
  claude: "runs.engine.claude",
  codex: "runs.engine.codex",
  openrouter: "runs.engine.openrouter",
  synthetic: "runs.engine.synthetic",
  // The career-native deterministic backend shares the "Deterministisch" label with `synthetic`.
  noop: "runs.engine.synthetic",
}

const ROLE_KEY: Record<string, CareerKey> = {
  system: "runs.role.system",
  user: "runs.role.user",
  assistant: "runs.role.assistant",
}

/** Turn labels emitted by the capture seams → German chips (raw token when unmapped). */
const LABEL_KEY: Record<string, CareerKey> = {
  extract: "runs.label.extract",
  verify: "runs.label.verify",
  match: "runs.label.match",
  "org-research": "runs.label.orgResearch",
  "org-summary": "runs.label.orgSummary",
  "opportunity-brief": "runs.label.brief",
  triage: "runs.label.triage",
  "discovery-triage": "runs.label.triage",
  preselect: "runs.label.preselect",
  author: "runs.label.author",
  kurzprofil: "runs.label.kurzprofil",
}

/** Activity → German title (prefix-matched; a default for the unmapped). */
function activityKey(activityType?: string): CareerKey {
  if (!activityType) return "runs.activity.default"
  if (activityType.startsWith("search")) return "runs.activity.search"
  if (activityType.startsWith("ingest")) return "runs.activity.ingest"
  if (activityType.startsWith("workshop:probe")) return "runs.activity.workshopCard"
  if (activityType.startsWith("workshop")) return "runs.activity.workshop"
  if (activityType.startsWith("application")) return "runs.activity.generate"
  return "runs.activity.default"
}

// ─── Formatting ─────────────────────────────────────────────────────────────────

/** Cached input tokens are billed at a fraction of a fresh token → de-weight them in the split. */
const CACHE_TOKEN_WEIGHT = 0.1

function fmtEur(usd: number, rate: number): string {
  const eur = usd * rate
  if (eur <= 0) return "€0"
  if (eur < 0.0001) return "< €0,0001"
  return `€${eur.toFixed(eur < 1 ? 4 : 2)}`.replace(".", ",")
}
function fmtNum(n: number): string {
  return n.toLocaleString("de-DE")
}
function fmtRate(rate: number): string {
  return rate.toFixed(2).replace(".", ",")
}
function fmtWhen(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })
}

const runCost = (r: RunView): number => r.steps.reduce((sum, s) => sum + (s.costUsd ?? 0), 0)

function costSplit(step: RunStepView): { input: number; output: number; cacheAdjusted: boolean } | null {
  const cost = step.costUsd ?? 0
  const pt = step.promptTokens ?? 0
  const ct = step.completionTokens ?? 0
  if (cost <= 0 || pt + ct <= 0) return null
  const cached = Math.min(step.cachedTokens ?? 0, pt)
  const effInput = pt - cached + cached * CACHE_TOKEN_WEIGHT
  const denom = effInput + ct
  const input = denom > 0 ? (cost * effInput) / denom : 0
  return { input, output: cost - input, cacheAdjusted: cached > 0 }
}

// ─── Copy-to-clipboard (Clipboard API → execCommand fallback) ───────────────────

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // fall through to the execCommand path
  }
  try {
    const ta = document.createElement("textarea")
    ta.value = text
    ta.setAttribute("readonly", "")
    ta.style.position = "fixed"
    ta.style.left = "-9999px"
    document.body.appendChild(ta)
    ta.focus()
    ta.select()
    const ok = document.execCommand("copy")
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}

// ─── ViewModel ──────────────────────────────────────────────────────────────────

export class RunsVm implements ViewModel {
  readonly runs: Signal<readonly RunView[] | null> = signal<readonly RunView[] | null>(null)
  readonly rate: Signal<number> = signal(0.92)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly loading: Signal<boolean> = signal(true)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]

  constructor(i18n: CareerI18n) {
    this.t = i18n.t
    this.renderDeps = [this.runs, this.rate, this.error, this.loading]
  }

  attach(): () => void {
    void this.load()
    return () => {}
  }

  async load(): Promise<void> {
    this.loading.set(true)
    this.error.set(null)
    try {
      const data = await apiGet<{ runs: unknown[]; usdEurRate: unknown }>("/api/runs")
      const runs = Array.isArray(data.runs) ? data.runs.map(parseRun).filter((r): r is RunView => r !== null) : []
      this.runs.set(runs)
      this.rate.set(typeof data.usdEurRate === "number" && data.usdEurRate > 0 ? data.usdEurRate : 0.92)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loading.set(false)
    }
  }

  totals(runs: readonly RunView[]): { runs: number; calls: number; tokens: number; cost: number } {
    return {
      runs: runs.length,
      calls: runs.reduce((n, r) => n + r.steps.length, 0),
      tokens: runs.reduce((n, r) => n + r.steps.reduce((s, st) => s + (st.promptTokens ?? 0) + (st.completionTokens ?? 0), 0), 0),
      cost: runs.reduce((n, r) => n + runCost(r), 0),
    }
  }
}

// ─── Element ────────────────────────────────────────────────────────────────────

export class CareerRuns extends KitElement<RunsVm> {
  protected createViewModel(ctx: KitContext): RunsVm {
    return new RunsVm(ctx.inject(I18nToken))
  }

  private engineChip(vm: RunsVm, engine: RunEngine): TemplateResult {
    const key = ENGINE_KEY[engine]
    return html`<span class="run-engine run-engine--${engine}" lang="de">${key ? vm.t(key) : engine}</span>`
  }

  private labelChip(vm: RunsVm, label: string): TemplateResult {
    const key = LABEL_KEY[label]
    return html`<span class="run-label" lang="de">${key ? vm.t(key) : label}</span>`
  }

  private metric(vm: RunsVm, labelKey: CareerKey, value: string, hint?: string): TemplateResult {
    return html`
      <div class="run-metric">
        <span class="run-metric-label" lang="de">${vm.t(labelKey)}</span>
        <span class="run-metric-value">${value}</span>
        ${hint ? html`<span class="run-metric-hint" lang="de">${hint}</span>` : nothing}
      </div>
    `
  }

  private tokenBar(step: RunStepView): TemplateResult | typeof nothing {
    const pt = step.promptTokens ?? 0
    const ct = step.completionTokens ?? 0
    const cached = Math.min(step.cachedTokens ?? 0, pt)
    const total = pt + ct
    if (total <= 0) return nothing
    const pct = (n: number): string => `${(n / total) * 100}%`
    return html`
      <div class="run-tokenbar">
        <span class="run-tokenbar-seg run-tokenbar-seg--fresh" style=${`width:${pct(pt - cached)}`}></span>
        ${cached > 0 ? html`<span class="run-tokenbar-seg run-tokenbar-seg--cache" style=${`width:${pct(cached)}`}></span>` : nothing}
        <span class="run-tokenbar-seg run-tokenbar-seg--answer" style=${`width:${pct(ct)}`}></span>
      </div>
    `
  }

  private paramGrid(vm: RunsVm, step: RunStepView): TemplateResult {
    const p = step.params
    const rows: Array<[string, string]> = []
    if (p?.temperature != null) rows.push(["temperature", String(p.temperature)])
    if (p?.topP != null) rows.push(["top_p", String(p.topP)])
    if (p?.maxTokens != null) rows.push(["max_tokens", fmtNum(p.maxTokens)])
    if (p?.reasoningEffort) rows.push(["reasoning", p.reasoningEffort])
    if (p?.jsonSchemaName) rows.push(["json_schema", p.jsonSchemaName])
    if (p?.jsonSchemaStrict != null) rows.push(["strict", p.jsonSchemaStrict ? "true" : "false"])
    if (rows.length === 0) return html`<p class="run-params-none" lang="de">${vm.t("runs.params.none")}</p>`
    return html`
      <div class="run-params-grid">
        ${rows.map(
          ([k, v]) => html`<div class="run-param"><span class="run-param-key">${k}</span><span class="run-param-val">${v}</span></div>`,
        )}
      </div>
    `
  }

  private promptBlock(vm: RunsVm, msg: RunPromptMsg): TemplateResult {
    const roleKey = ROLE_KEY[msg.role]
    return html`
      <div class="run-prompt-msg">
        <div class="run-prompt-head">
          <span class="run-prompt-role" lang="de">${roleKey ? vm.t(roleKey) : msg.role}</span>
          <button type="button" data-act="copy-prompt" class="run-copy" lang="de">${vm.t("runs.prompt.copy")}</button>
        </div>
        <pre class="run-prompt-pre">${msg.content}</pre>
      </div>
    `
  }

  private costBlock(vm: RunsVm, step: RunStepView, rate: number): TemplateResult {
    const split = costSplit(step)
    const cached = step.cachedTokens ?? 0
    return html`
      <div class="run-cost">
        <div class="run-cost-head">
          <span class="run-cost-title" lang="de">${vm.t("runs.cost.title")}</span>
          <span class="run-cost-rate" lang="de">${vm.t("runs.cost.rate", { rate: fmtRate(rate) })}</span>
        </div>
        ${split
          ? html`
              <div class="run-cost-body">
                <span class="run-cost-item" lang="de">${vm.t("runs.cost.input")} <b>${fmtEur(split.input, rate)}</b></span>
                <span class="run-cost-item" lang="de">${vm.t("runs.cost.output")} <b>${fmtEur(split.output, rate)}</b></span>
                <span class="run-cost-item run-cost-total" lang="de">${vm.t("runs.cost.total")} <b>${fmtEur(step.costUsd ?? 0, rate)}</b></span>
                <span class="run-cost-note" lang="de">${vm.t("runs.cost.estimated")}${split.cacheAdjusted ? ` · ${vm.t("runs.cost.cacheNote")}` : ""}</span>
              </div>
            `
          : html`<p class="run-cost-free" lang="de">${vm.t("runs.cost.free")}${cached > 0 ? ` · ${vm.t("runs.cacheChip", { n: fmtNum(cached) })}` : ""}</p>`}
      </div>
    `
  }

  private step(vm: RunsVm, step: RunStepView, index: number, rate: number): TemplateResult {
    const cached = step.cachedTokens ?? 0
    return html`
      <details class="run-step">
        <summary class="run-step-summary">
          <span class="run-step-index">#${index + 1}</span>
          ${this.engineChip(vm, step.engine)}
          ${step.label ? this.labelChip(vm, step.label) : nothing}
          <span class="run-step-model">${step.model}</span>
          <span class="run-step-cost">${fmtEur(step.costUsd ?? 0, rate)}</span>
        </summary>
        <div class="run-step-body">
          ${step.purpose ? html`<p class="run-step-purpose" lang="de">${step.purpose}</p>` : nothing}

          <div class="run-metrics">
            ${this.metric(vm, "runs.metric.input", step.promptTokens != null ? fmtNum(step.promptTokens) : "—", vm.t("runs.metric.tokens"))}
            ${this.metric(vm, "runs.metric.output", step.completionTokens != null ? fmtNum(step.completionTokens) : "—", vm.t("runs.metric.tokens"))}
            ${this.metric(vm, "runs.metric.cache", cached > 0 ? fmtNum(cached) : step.cacheHit ? vm.t("runs.metric.yes") : "—")}
            ${this.metric(vm, "runs.metric.latency", step.latencyMs != null ? `${fmtNum(Math.round(step.latencyMs))} ms` : "—")}
          </div>

          ${this.tokenBar(step)}
          ${this.costBlock(vm, step, rate)}

          <div class="run-params">
            <span class="run-section-label" lang="de">${vm.t("runs.params.title")}</span>
            ${this.paramGrid(vm, step)}
          </div>

          <div class="run-prompts" data-step-prompts>
            <div class="run-prompts-head">
              <span class="run-section-label" lang="de">${vm.t("runs.prompt.title")}${step.promptTruncated ? ` · ${vm.t("runs.prompt.truncatedTag")}` : ""}</span>
              ${step.prompt && step.prompt.length > 0
                ? html`<button type="button" data-act="copy-all-prompt" class="run-copy" lang="de">${vm.t("runs.prompt.copyAll")}</button>`
                : nothing}
            </div>
            ${step.prompt && step.prompt.length > 0
              ? html`
                  ${repeat(step.prompt, (_m, i) => i, (m) => this.promptBlock(vm, m))}
                  ${step.promptTruncated ? html`<p class="run-prompt-note" lang="de">${vm.t("runs.prompt.truncatedNote")}</p>` : nothing}
                `
              : html`<p class="run-prompt-none" lang="de">${vm.t("runs.prompt.none")}</p>`}
          </div>

          ${step.contextNote
            ? html`
                <div class="run-context">
                  <span class="run-section-label" lang="de">${vm.t("runs.why")}</span>
                  <p class="run-context-note" lang="de">${step.contextNote}</p>
                </div>
              `
            : nothing}
        </div>
      </details>
    `
  }

  private run(vm: RunsVm, run: RunView, rate: number, open: boolean): TemplateResult {
    const n = run.steps.length
    return html`
      <details class="run" ?open=${open}>
        <summary class="run-summary">
          <div class="run-summary-head">
            <h3 class="run-title" lang="de">${vm.t(activityKey(run.activityType))}</h3>
            <span class="run-when">${fmtWhen(run.createdAt)}</span>
          </div>
          <div class="run-summary-meta">
            <span class="run-calls" lang="de">${vm.t(n === 1 ? "runs.callsOne" : "runs.calls", { n: fmtNum(n) })}</span>
            <span class="run-total-cost">${fmtEur(runCost(run), rate)}</span>
          </div>
        </summary>
        <div class="run-steps">
          ${repeat(run.steps, (_s, i) => `${run.id}:${i}`, (s, i) => this.step(vm, s, i, rate))}
        </div>
      </details>
    `
  }

  protected render(vm: RunsVm): TemplateResult {
    const runs = vm.runs.value
    const rate = vm.rate.value
    const err = vm.error.value

    const body = ((): TemplateResult => {
      if (err) {
        return html`
          <div class="runs-error-box">
            <p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: err })}</p>
            <button type="button" data-act="reload-runs" lang="de">${vm.t("runs.retry")}</button>
          </div>
        `
      }
      if (vm.loading.value && runs === null) {
        return html`<p class="surface-empty" lang="de">${vm.t("runs.loading")}</p>`
      }
      if (!runs || runs.length === 0) {
        return html`<p class="surface-empty" lang="de">${vm.t("runs.empty")}</p>`
      }
      const t = vm.totals(runs)
      return html`
        <div class="runs-ledger">
          ${this.metric(vm, "runs.ledger.runs", fmtNum(t.runs))}
          ${this.metric(vm, "runs.ledger.calls", fmtNum(t.calls))}
          ${this.metric(vm, "runs.ledger.tokens", fmtNum(t.tokens))}
          ${this.metric(vm, "runs.ledger.total", fmtEur(t.cost, rate), vm.t("runs.cost.rate", { rate: fmtRate(rate) }))}
        </div>
        <div class="runs-list">
          ${repeat(runs, (r) => r.id, (r, i) => this.run(vm, r, rate, i === 0))}
        </div>
      `
    })()

    return html`
      <section class="surface runs" aria-labelledby="runs-title">
        <h1 id="runs-title" class="surface-title" lang="de">${vm.t("surface.laeufe.title")}</h1>
        <p class="surface-lead" lang="de">${vm.t("surface.laeufe.lead")}</p>
        ${body}
      </section>
    `
  }

  protected override actions(vm: RunsVm): ActionMap {
    const feedback = async (btn: HTMLElement, text: string): Promise<void> => {
      const ok = await copyToClipboard(text)
      const prev = btn.textContent
      btn.textContent = ok ? vm.t("runs.prompt.copied") : vm.t("runs.prompt.copyFailed")
      setTimeout(() => {
        btn.textContent = prev
      }, 1800)
    }
    return {
      "reload-runs": () => void vm.load(),
      "copy-prompt": (_payload, event) => {
        const btn = event.target as HTMLElement
        const pre = btn.closest(".run-prompt-msg")?.querySelector("pre")
        if (pre) void feedback(btn, pre.textContent ?? "")
      },
      "copy-all-prompt": (_payload, event) => {
        const btn = event.target as HTMLElement
        const container = btn.closest("[data-step-prompts]")
        const pres = container ? [...container.querySelectorAll("pre")] : []
        const text = pres.map((p) => p.textContent ?? "").join("\n\n")
        void feedback(btn, text)
      },
    }
  }
}
